import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { GET_SELLER_STORE } from "../../api/seller-store"; // 매장 정보 가져오기
import { CREATE_PACK, GET_PACK_BY_SELLER } from "../../api/pack-queries";
import { useNavigate } from "react-router-dom";
import Header from "../../../../components/common/header"; // Header 컴포넌트 임포트
import { toast } from "react-toastify";
import {
  HiMinus,
  HiPlus,
  HiXMark,
  HiChevronDown,
  HiChevronUp,
} from "react-icons/hi2";
import mahiDownloadLogo from "../../../../assets/images/mahi-download-logo-512.png";
import { notify } from "../../../../utils/toast";
import { roundUpToHundred } from "../../../../utils/price";

const PreOrderPackManagement: React.FC = () => {
  const navigate = useNavigate();
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [cancelMessage, setCancelMessage] = useState("");
  const [packType, setPackType] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState(10000);
  const [quantity, setQuantity] = useState("");
  const [customQuantity, setCustomQuantity] = useState("");
  const [isCustomQuantity, setIsCustomQuantity] = useState(false);
  const [isReservationOff, setIsReservationOff] = useState(false);
  const [dailyOrderLimit, setDailyOrderLimit] = useState("");
  const [discountRate, setDiscountRate] = useState(0.6);
  const [representativeMenus, setRepresentativeMenus] = useState(["", "", ""]);
  const [showSettlementDetail, setShowSettlementDetail] = useState(false);

  const adjustPriceByAmount = (amount: number) => {
    const currentPrice = price || 0;
    const newPrice = currentPrice + amount;

    if (newPrice < 2000) {
      toast.error("팩 가격은 2,000원 이상이어야 해요!");
      setPrice(2000);
      return;
    }

    setPrice(newPrice);
  };

  const addRepresentativeMenu = () => {
    setRepresentativeMenus([...representativeMenus, ""]);
  };

  const removeRepresentativeMenu = (index: number) => {
    const newMenus = representativeMenus.filter((_, i) => i !== index);

    // 모든 메뉴를 삭제했을 때는 기본 3개 빈 칸으로 복원
    if (newMenus.length === 0) {
      setRepresentativeMenus(["", "", ""]);
    } else {
      setRepresentativeMenus(newMenus);
    }
  };

  const updateRepresentativeMenu = (index: number, value: string) => {
    const newMenus = [...representativeMenus];
    newMenus[index] = value;
    setRepresentativeMenus(newMenus);
  };

  const [createPack] = useMutation(CREATE_PACK);
  const {
    data: storeData,
    loading,
    error,
  } = useQuery(GET_SELLER_STORE, {
    fetchPolicy: "network-only",
  });

  useQuery(GET_PACK_BY_SELLER, {
    onCompleted: (data) => {
      if (data.packBySeller) {
        const pack = data.packBySeller;
        setPackType(pack.category);
        setDescription(pack.description);
        setPrice(pack.price);
        if (pack.quantity <= 4 && pack.quantity >= 2) {
          setQuantity(pack.quantity.toString());
        } else {
          setIsCustomQuantity(true);
          setCustomQuantity(pack.quantity.toString());
        }
        setIsReservationOff(pack.isTomorrowReservationEnabled === false);
        setDailyOrderLimit(
          pack.maxOrderQuantity ? pack.maxOrderQuantity.toString() : "none",
        );
        setCancelMessage(
          pack.cancelMessage ||
            "저희 가게를 찾아주시고 기다려주셨을 텐데, 오늘은 준비한 재고가 모두 소진되어 부득이하게 예약을 취소드리게 되었어요. 소중한 주문 감사드려요.", // 기본 메시지
        );

        // 할인율 초기값: pack에 저장된 discountRate를 그대로 노출
        if (pack.discountRate != null) {
          setDiscountRate(pack.discountRate / 100);
        }

        // packItems 데이터로 대표메뉴 초기화
        if (pack.packItems && pack.packItems.length > 0) {
          const menuNames = pack.packItems.map((item: any) => item.name);
          setRepresentativeMenus(menuNames);
        } else {
          // packItems가 없으면 기본 3개 빈 칸으로 설정
          setRepresentativeMenus(["", "", ""]);
        }
      }
    },
    fetchPolicy: "network-only",
  });
  // 매장이 없을 경우 처리
  if (loading) return <p>로딩 중...</p>;
  if (error) return <p>오류: {error.message}</p>;
  if (!storeData?.getSellerStore) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <p className="text-lg mb-6 text-gray-700">
          매장이 아직 등록되지 않았습니다.
        </p>
        <button
          onClick={() => navigate("/boss/my/store-management")}
          className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition duration-300"
        >
          매장 추가하러 가기
        </button>
      </div>
    );
  }

  const packDiscountRateTier =
    storeData.getSellerStore.packDiscountRateTier ?? "DEFAULT";
  const is50PercentStore = packDiscountRateTier === "TIER_50";
  const is40PercentStore = packDiscountRateTier === "TIER_40";

  const isFormValid =
    packType &&
    description !== "" &&
    price &&
    (quantity || (isCustomQuantity && customQuantity));

  const handleSubmit = () => {
    if (price < 2000) {
      toast.error("팩 가격은 2,000원 이상이어야 해요!");
      return;
    }

    // 할인율을 백분율로 변환 (discountRate는 소수값 0.5, 0.55 또는 0.6)
    const discountRatePercent = Math.floor(discountRate * 100); // 50, 55 또는 60
    const roundedDiscountPrice = roundUpToHundred(
      Math.round(price * (1 - discountRate)),
    );
    const userPrice = roundedDiscountPrice + 900;
    // Call the mutation
    createPack({
      variables: {
        name: packType,
        category: packType,
        price: price,
        discountPrice: roundedDiscountPrice,
        discountRate: discountRatePercent, // 백분율로 전송 (50, 55 또는 60)
        userPrice: userPrice,
        quantity: parseInt(isCustomQuantity ? customQuantity : quantity),
        description: description,
        isTomorrowReservationEnabled: isReservationOff === true ? false : true,
        maxOrderQuantity:
          dailyOrderLimit === "none" ? null : Number(dailyOrderLimit),
        cancelMessage: cancelMessage,
        packItems: representativeMenus,
      },
      onCompleted: () => {
        if (storeData.getSellerStore.reserveTime) {
          navigate("/boss/pre-order/home");
          notify("팩 설정이 완료되었습니다.");
        } else {
          navigate("/boss/pre-order/pickup-time-update");
        }
      },
    });
  };
  console.log(discountRate);

  return (
    <div className="bg-gray-50 min-h-screen pb-20">
      <Header
        title="예약 판매 설정"
        showBackButton
        onBackClick={() => navigate("/boss/pre-order/home", { replace: true })}
      />
      <div className="p-6 max-w-xl mx-auto bg-white rounded-lg shadow-md">
        <div className="mb-8">
          <label className="block text-gray-700 font-semibold">팩 종류</label>
          <p className="text-gray-500 mb-2">어떤 음식을 담나요?</p>
          <div className="flex flex-col space-y-2">
            <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
              <input
                type="radio"
                value="베이커리"
                checked={packType === "베이커리"}
                onChange={(e) => setPackType(e.target.value)}
                className="mr-2"
              />
              베이커리
            </label>
            <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
              <input
                type="radio"
                value="식사류"
                checked={packType === "식사류"}
                onChange={(e) => setPackType(e.target.value)}
                className="mr-2"
              />
              식사류
            </label>
            <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
              <input
                type="radio"
                value="샐러드"
                checked={packType === "샐러드"}
                onChange={(e) => setPackType(e.target.value)}
                className="mr-2"
              />
              샐러드
            </label>
            <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
              <input
                type="radio"
                value="정육점"
                checked={packType === "정육점"}
                onChange={(e) => setPackType(e.target.value)}
                className="mr-2"
              />
              정육점
            </label>
            <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
              <input
                type="radio"
                value="반찬과 요리"
                checked={packType === "반찬과 요리"}
                onChange={(e) => setPackType(e.target.value)}
                className="mr-2"
              />
              반찬과 요리
            </label>
            <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
              <input
                type="radio"
                value="기타"
                checked={packType === "기타"}
                onChange={(e) => setPackType(e.target.value)}
                className="mr-2"
              />
              기타
            </label>
          </div>
        </div>

        <div className="mb-8">
          <label className="block text-gray-700 font-semibold">대표메뉴</label>
          <p className="text-gray-500 mb-2">
            팩에 들어갈 대표 메뉴들을 입력해주세요
          </p>
          <div className="space-y-3">
            {representativeMenus.map((menu, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="text"
                  value={menu}
                  onChange={(e) =>
                    updateRepresentativeMenu(index, e.target.value)
                  }
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 transition duration-200"
                  placeholder={`대표메뉴 ${index + 1}`}
                />
                <button
                  onClick={() => removeRepresentativeMenu(index)}
                  className="w-10 h-10 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition duration-200"
                >
                  <HiMinus className="w-5 h-5" />
                </button>
              </div>
            ))}
            <button
              onClick={addRepresentativeMenu}
              className="w-full h-10 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition duration-200 border-2 border-dashed border-blue-300"
            >
              <HiPlus className="w-5 h-5 mr-2" />
              메뉴 추가하기
            </button>
          </div>
        </div>

        <div className="mb-8">
          <label className="block text-gray-700 font-semibold">설명</label>
          <p className="text-gray-500">어떤 메뉴가 주로 담기나요?</p>
          <p className="text-green-500 text-md mb-2">
            여기 설명을 자세하게 적어주시면 판매율이 15% 정도 올라요!
          </p>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full h-36 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 transition duration-200"
            placeholder="예시) 매일 빵을 굽는 0000입니다.  샌드위치류, 크림소금빵류가 주로 올라옵니다. 당일 제조 이기 때문에 냉장에서 3일까지 드실 수 있습니다.  고객님들은 저렴한 가격에 드실 수 있고, 저희는 버리는 음식이 줄어들어 기쁩니다!"
          />
        </div>

        {/* 할인율 선택 */}
        <div className="mb-8">
          <label className="block text-gray-700 font-semibold mb-2">
            할인율 선택
          </label>
          <p className="text-gray-500 mb-2">
            고객에게 판매할 할인율을 선택해주세요
          </p>
          <div className="flex flex-col space-y-2">
            {is40PercentStore &&
              [0.4, 0.45, 0.5, 0.55, 0.6].map((rate) => (
                <label
                  key={rate}
                  className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100"
                >
                  <input
                    type="radio"
                    value={rate}
                    checked={discountRate === rate}
                    onChange={() => setDiscountRate(rate)}
                    className="mr-2"
                  />
                  {Math.round(rate * 100)}% 할인
                </label>
              ))}
            {!is40PercentStore && (
              <>
                {is50PercentStore && (
                  <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
                    <input
                      type="radio"
                      value="0.5"
                      checked={discountRate === 0.5}
                      onChange={(e) => setDiscountRate(0.5)}
                      className="mr-2"
                    />
                    50% 할인
                  </label>
                )}
                <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
                  <input
                    type="radio"
                    value="0.55"
                    checked={discountRate === 0.55}
                    onChange={(e) => setDiscountRate(0.55)}
                    className="mr-2"
                  />
                  55% 할인
                </label>
                <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
                  <input
                    type="radio"
                    value="0.6"
                    checked={discountRate === 0.6}
                    onChange={(e) => setDiscountRate(0.6)}
                    className="mr-2"
                  />
                  60% 할인
                </label>
              </>
            )}
          </div>
        </div>

        <div className="mb-8">
          <label className="block text-gray-700 font-semibold">가격</label>
          <p className="text-gray-500 mb-2">
            버려질 뻔 한 음식을 남김없이 소비되도록 하기 위해{" "}
            {Math.round(discountRate * 100)}%의 가격으로 고객들에게 판매합니다.
            설정한 금액만큼의 혹은 더 많은 가치의 상품이 담겨야합니다! <br />
            <span className="text-md text-gray-500">
              저희도 그 날 사장님의 재고를 모두 팔 수 있도록 최선을 다할게요😉
            </span>
          </p>
          <div className="flex flex-col space-y-2">
            <div className="flex flex-col rounded-lg p-2 bg-gray-100 border border-gray-300">
              <div className="relative mb-2">
                <div className="flex items-start gap-2">
                  <button
                    className="w-12 h-12 flex items-center justify-center bg-white border border-gray-300 rounded-lg active:bg-gray-100 transition duration-100"
                    onClick={() => adjustPriceByAmount(-100)}
                  >
                    <HiMinus className="w-6 h-6 text-gray-600" />
                  </button>

                  <div className="flex-1 flex flex-col">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        ₩
                      </span>
                      <input
                        type="number"
                        className="w-full h-12 pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition duration-200 text-lg font-medium"
                        value={price}
                        onChange={(e) => {
                          if (parseInt(e.target.value) <= 0) {
                            toast.error("가격은 0원 이하가 될 수 없어요!");
                          } else {
                            setPrice(parseInt(e.target.value));
                          }
                        }}
                        placeholder="가격을 입력해주세요"
                      />
                    </div>

                    <div className="flex justify-end">
                      <p className="text-gray-500 text-sm mt-0.5">
                        *클릭하여 가격 직접 입력하기
                      </p>
                    </div>
                  </div>
                  <button
                    className="w-12 h-12 flex items-center justify-center bg-white border border-gray-300 rounded-lg active:bg-gray-100 transition duration-100"
                    onClick={() => adjustPriceByAmount(+100)}
                  >
                    <HiPlus className="w-6 h-6 text-gray-600" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-300">
                  <span className="text-gray-700 font-medium">정가</span>
                  <span className="font-bold text-lg text-gray-600">
                    {price > 0 ? price.toLocaleString() : "0"}원
                  </span>
                </div>

                <div className="flex flex-col p-3 bg-white rounded-lg border border-gray-300">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-700 font-medium">
                      사장님께 정산드리는 금액
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-lg text-green-600">
                        {price > 0
                          ? Math.floor(
                              roundUpToHundred(
                                Math.round(price * (1 - discountRate)),
                              ) * 0.91,
                            ).toLocaleString()
                          : "0"}
                        원
                      </span>
                      <button
                        onClick={() =>
                          setShowSettlementDetail(!showSettlementDetail)
                        }
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showSettlementDetail ? (
                          <HiChevronUp className="w-5 h-5" />
                        ) : (
                          <HiChevronDown className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  {showSettlementDetail && (
                    <div className="text-xs text-gray-500 mt-1 space-y-1 pt-2 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <span>
                          1. {Math.round(discountRate * 100)}% 할인 적용
                        </span>
                        <span className="font-medium">
                          {price > 0
                            ? Math.round(
                                price * (1 - discountRate),
                              ).toLocaleString()
                            : "0"}
                          원
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>2. 100원 단위 올림</span>
                        <span className="font-medium">
                          {price > 0
                            ? roundUpToHundred(
                                Math.round(price * (1 - discountRate)),
                              ).toLocaleString()
                            : "0"}
                          원
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>3. 수수료(9%) 차감</span>
                        <span className="font-medium">
                          {price > 0
                            ? Math.floor(
                                roundUpToHundred(
                                  Math.round(price * (1 - discountRate)),
                                ) * 0.91,
                              ).toLocaleString()
                            : "0"}
                          원
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 font-semibold">개수</label>
          <p className="text-gray-500 mb-2">
            <span className="text-green-500 font-medium rounded-lg py-0.5 px-1 border ml-0.5">
              정가 {price > 0 ? price.toLocaleString() : "0"}원
            </span>
            팩이 하루에 몇개 나오나요?
          </p>
          <div className="flex flex-col space-y-2">
            <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
              <input
                type="radio"
                value="2"
                checked={quantity === "2" && !isCustomQuantity}
                onChange={(e) => {
                  setQuantity(e.target.value);
                  setIsCustomQuantity(false);
                }}
                className="mr-2"
              />
              하루에 2개
            </label>
            <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
              <input
                type="radio"
                value="3"
                checked={quantity === "3" && !isCustomQuantity}
                onChange={(e) => {
                  setQuantity(e.target.value);
                  setIsCustomQuantity(false);
                }}
                className="mr-2"
              />
              하루에 3개
            </label>
            <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
              <input
                type="radio"
                value="4"
                checked={quantity === "4" && !isCustomQuantity}
                onChange={(e) => {
                  setQuantity(e.target.value);
                  setIsCustomQuantity(false);
                }}
                className="mr-2"
              />
              하루에 4개
            </label>
            <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
              <input
                type="radio"
                checked={isCustomQuantity}
                onChange={() => {
                  setIsCustomQuantity(true);
                  setQuantity("");
                }}
                className="mr-2"
              />
              직접 입력
              {isCustomQuantity && (
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={customQuantity}
                  onChange={(e) => {
                    if (parseInt(e.target.value) <= 0) {
                      alert("수량은 1개 이상 입력해주세요");
                    } else {
                      setCustomQuantity(e.target.value);
                    }
                  }}
                  className="ml-2 w-20 px-2 py-1 border border-gray-300 rounded"
                  placeholder="개수"
                />
              )}
            </label>
          </div>
          <div className="mt-8">
            <label className="block text-gray-700 font-semibold">
              내일 예약 꺼놓기
            </label>
            <p className="text-gray-500 mb-2">오늘예약만 받을 수 있어요</p>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={isReservationOff}
                onChange={() => setIsReservationOff(!isReservationOff)}
                className="mr-2 w-4 h-4"
              />
              <span className="text-gray-500">
                예약을 꺼놓으려면 체크하세요
              </span>
            </div>
          </div>
          {/* 일일 최대 주문 팩 수량 설정 (DB 플래그로 기능 오픈) */}
          {storeData.getSellerStore.isPackOrderLimitConfigEnabled && (
            <div className="mt-8">
              <label className="block text-gray-700 font-semibold">
                일일 최대 주문 팩 수량 설정
              </label>
              <p className="text-gray-500 mb-2">
                일일 유저당 최대 주문 수량을 설정할 수 있어요. <br />
                한명이 싹쓸이하는 것을 방지합니다!👋
              </p>
              <select
                value={dailyOrderLimit}
                onChange={(e) => setDailyOrderLimit(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="">선택하세요</option>
                <option value="1">1개</option>
                <option value="2">2개</option>
                <option value="3">3개</option>
                <option value="4">4개</option>
                <option value="none">없음</option>
              </select>
            </div>
          )}
        </div>

        <div className="mt-8">
          <div className="mt-4">
            <label className="block text-gray-700 font-semibold mb-2">
              예약팩 취소 메시지 입력
            </label>

            <p className="text-gray-500 mb-1">
              예약팩을 취소할 경우 손님에게 발송될 안내 메시지를 설정해주세요.
            </p>
            <p className="text-gray-500">
              오랜 시간 기다려주신 손님의 기대를 생각하며,{" "}
              <span className="font-bold">따뜻하고 진심 어린 말</span>로
              아쉬움을 덜어주세요 😊
            </p>

            <textarea
              value={cancelMessage}
              onChange={(e) => setCancelMessage(e.target.value)}
              placeholder="손님에게 전달할 취소 메시지를 입력해주세요"
              className="w-full p-3 border border-gray-300 rounded-lg resize-none mt-2"
              rows={4}
            />
          </div>

          {/* 미리보기 버튼 */}
          <div className="flex justify-end mt-1">
            <button
              onClick={() => setIsPreviewModalOpen(true)}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
            >
              알림톡 미리보기
            </button>
          </div>
        </div>
      </div>

      {/* 알림톡 미리보기 모달 */}
      {isPreviewModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-800">
                  알림톡 미리보기
                </h3>
                <button
                  onClick={() => setIsPreviewModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <HiXMark className="w-6 h-6 text-gray-600" />
                </button>
              </div>

              {/* 알림톡 미리보기 */}
              <div className="bg-gray-100 px-2 py-4 rounded-lg">
                <div className="flex items-start space-x-3">
                  {/* 프로필 이미지 */}
                  <div className="w-10 h-10 bg-primary rounded-[1.1rem] flex items-center justify-center flex-shrink-0">
                    <img src={mahiDownloadLogo} alt="" className="w-8 h-8" />
                  </div>

                  {/* 발신자 정보 */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-black font-semibold text-base">
                        마감히어로
                      </span>
                    </div>

                    {/* 메시지 말풍선 */}
                    <div className="bg-white text-black p-4 rounded-lg relative max-w-md shadow-sm">
                      <div className="text-md">
                        <div className="text-gray-500 text-xs">취소 알림</div>
                        <div className="font-bold text-lg">주문 취소 안내</div>
                        <hr className="mt-1" />
                        <div className="text-base mt-2 whitespace-pre-wrap">
                          취소 사유:{" "}
                          <span className="font-medium italic">
                            {cancelMessage}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setIsPreviewModalOpen(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="fixed bottom-0 left-0 w-full p-4 bg-white">
        <button
          onClick={handleSubmit}
          disabled={!isFormValid}
          className={`w-full h-[60px] flex items-center justify-center rounded-3xl border text-white font-bold leading-snug ${
            isFormValid ? "bg-[#1562fc]" : "bg-gray-400"
          }`}
        >
          저장
        </button>
      </div>
    </div>
  );
};

export default PreOrderPackManagement;
